import React from 'react';
import { X, CheckCircle, ArrowRight, User, MapPin, Package, Printer, Warehouse, Store, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Reservation, InventoryItem, LocationInfo } from '../../types';

interface ColorSize {
  id: string;
  name: string;
}

interface TransferConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  transfer: Reservation | null;
  items: InventoryItem[];
  colors: ColorSize[];
  sizes: ColorSize[];
  locations: LocationInfo[];
  createdByName: string;
}

export const TransferConfirmationModal: React.FC<TransferConfirmationModalProps> = ({
  isOpen,
  onClose,
  transfer,
  items,
  colors,
  sizes,
  locations,
  createdByName
}) => {
  if (!isOpen || !transfer) return null;

  const sourceLocation = locations.find(l => l.id === transfer.source_location_id);
  const destinationLocation = locations.find(l => l.id === transfer.destination_location_id);

  const getColorName = (colorId?: string) => {
    if (!colorId) return '-';
    return colors.find(c => c.id === colorId)?.name || '-';
  };

  const getSizeName = (sizeId?: string) => {
    if (!sizeId) return '-';
    return sizes.find(s => s.id === sizeId)?.name || '-';
  };

  const getProductInfo = (productId: string) => {
    return items.find(item => item.id === productId);
  };

  const getLocationIcon = (name?: string) => {
    if (!name) return MapPin;
    const lowerName = name.toLowerCase();
    if (lowerName.includes('warehouse') || lowerName.includes('wearhouse')) return Warehouse;
    if (lowerName.includes('shop') || lowerName.includes('store')) return Store;
    return MapPin;
  };

  const printTransfer = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const itemsHtml = transfer.reservation_items?.map(item => {
      const product = getProductInfo(item.product_id);
      return `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${product?.name || 'Unknown'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${product?.reference_code || '-'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${getSizeName(product?.size_id)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${getColorName(product?.color_id)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center; font-weight: 600;">${item.quantity}</td>
        </tr>
      `;
    }).join('') || '<tr><td colspan="5" style="padding: 20px; text-align: center;">No items</td></tr>';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Transfer - ${transfer.reservation_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1f2937; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }
          .title { font-size: 28px; font-weight: 700; color: #111827; }
          .subtitle { color: #6b7280; margin-top: 4px; }
          .badge { display: inline-block; padding: 6px 16px; border-radius: 9999px; font-size: 12px; font-weight: 600; text-transform: uppercase; background: #dbeafe; color: #1e40af; }
          .transfer-flow { display: flex; align-items: center; justify-content: center; gap: 24px; margin: 32px 0; padding: 24px; background: #f9fafb; border-radius: 12px; }
          .location-box { text-align: center; padding: 16px 24px; background: white; border-radius: 12px; border: 2px solid #e5e7eb; }
          .location-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
          .location-name { font-size: 18px; font-weight: 600; color: #111827; }
          .arrow { font-size: 24px; color: #0d9488; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px; }
          .info-card { background: #f9fafb; border-radius: 12px; padding: 16px; }
          .info-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
          .info-value { font-size: 16px; font-weight: 600; color: #111827; }
          .section-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 16px; }
          .table-container { border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb; margin-bottom: 24px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f3f4f6; padding: 12px 10px; text-align: left; font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; }
          th:nth-child(5) { text-align: center; }
          td { padding: 10px; font-size: 14px; }
          .signature-section { margin-top: 48px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 48px; }
          .signature-box { border-top: 2px solid #e5e7eb; padding-top: 8px; }
          .signature-label { font-size: 12px; color: #6b7280; }
          .footer { margin-top: 40px; padding-top: 24px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
          .notice { background: #dbeafe; padding: 16px; border-radius: 12px; margin-top: 24px; }
          .notice-text { color: #1e40af; font-size: 14px; }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">Stock Transfer</div>
            <div class="subtitle">${transfer.reservation_number}</div>
          </div>
          <span class="badge">Pending Receipt</span>
        </div>

        <div class="transfer-flow">
          <div class="location-box">
            <div class="location-label">From</div>
            <div class="location-name">${sourceLocation?.name || 'Unknown'}</div>
          </div>
          <div class="arrow">&#8594;</div>
          <div class="location-box">
            <div class="location-label">To</div>
            <div class="location-name">${destinationLocation?.name || 'Unknown'}</div>
          </div>
        </div>

        <div class="info-grid">
          <div class="info-card">
            <div class="info-label">Requested By</div>
            <div class="info-value">${transfer.worker_name || createdByName}</div>
          </div>
          <div class="info-card">
            <div class="info-label">Created Date</div>
            <div class="info-value">${new Date(transfer.reserved_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
        </div>

        <div class="section-title">Transfer Items</div>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Reference</th>
                <th>Size</th>
                <th>Color</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
        </div>

        <div class="notice">
          <div class="notice-text">
            Items will be transferred to the destination location upon receipt confirmation.
          </div>
        </div>

        <div class="signature-section">
          <div class="signature-box">
            <div class="signature-label">Prepared By (Signature)</div>
          </div>
          <div class="signature-box">
            <div class="signature-label">Received By (Signature)</div>
          </div>
        </div>

        <div class="footer">
          <p>Printed on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const SourceIcon = getLocationIcon(sourceLocation?.name);
  const DestIcon = getLocationIcon(destinationLocation?.name);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-6 bg-gradient-to-br from-teal-500 to-cyan-600 text-white flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8" />
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <h2 className="text-2xl font-bold mb-1">Transfer Created!</h2>
          <p className="text-teal-100">Items are ready to be transferred</p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 mb-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Transfer Number
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-wider">
              {transfer.reservation_number}
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 mb-6 p-4 bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center mb-2">
                <SourceIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">From</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{sourceLocation?.name || 'Unknown'}</span>
            </div>

            <div className="flex-shrink-0">
              <ArrowRight className="h-8 w-8 text-teal-500" />
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-2">
                <DestIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">To</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{destinationLocation?.name || 'Unknown'}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Requested By</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {transfer.worker_name || createdByName}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                <p className="font-medium text-amber-600 dark:text-amber-400">
                  Pending Receipt
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Transfer Items ({transfer.reservation_items?.length || 0})
              </h3>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Product</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Reference</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Color</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {transfer.reservation_items?.map((item, idx) => {
                    const product = getProductInfo(item.product_id);
                    return (
                      <tr key={idx} className="bg-white dark:bg-gray-800">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">
                          {product?.name || 'Unknown Product'}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {product?.reference_code || '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {getSizeName(product?.size_id)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {getColorName(product?.color_id)}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-900 dark:text-gray-100 font-bold">
                          {item.quantity}
                        </td>
                      </tr>
                    );
                  })}
                  {(!transfer.reservation_items || transfer.reservation_items.length === 0) && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                        No items in this transfer
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Note:</strong> Items will be moved to the destination location once receipt is confirmed.
              The transfer slip can be printed for physical verification.
            </p>
          </div>
        </div>

        <div className="p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={printTransfer}
              className="flex-1"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Transfer Slip
            </Button>
            <Button
              variant="primary"
              onClick={onClose}
              className="flex-1"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
